import React, { useState, useEffect } from "react";
import { Database, Search, X, ChevronDown, ChevronUp, Network, HardDrive, Clock, AlertTriangle, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface NvrListSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    nvrList: any[];
    filter: "all" | "healthy";
}

// Child component for NVR card to handle individual network expand state
const NvrCard: React.FC<{ nvr: any }> = ({ nvr }) => {
    const [isNetworkExpanded, setIsNetworkExpanded] = useState(false);

    // Safely parse device if it's a string
    const device = typeof nvr.device === 'string' 
        ? (() => { try { return JSON.parse(nvr.device); } catch(e) { return {}; } })()
        : (nvr.device || {});

    // Safely parse network if it's a string
    const network = typeof nvr.network === 'string'
        ? (() => { try { return JSON.parse(nvr.network); } catch(e) { return {}; } })()
        : (nvr.network || {});

    return (
        <div className="bg-white border border-slate-200 hover:border-[#422568]/30 rounded-xl p-3.5 flex flex-col gap-3 transition-all shadow-sm hover:shadow group">
            {/* Header: Address & Status */}
            <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-[#422568]/10 text-[#422568] rounded-lg group-hover:scale-105 transition-all">
                        <Database className="w-3.5 h-3.5" />
                    </div>
                    <div>
                        <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight truncate max-w-[200px]">
                            {nvr.nvr_address}
                        </h4>
                        {network?.hostname && (
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-tight">
                                {network.hostname}
                            </span>
                        )}
                    </div>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase tracking-wider border shadow-sm ${
                    nvr.status === "online" 
                        ? "bg-emerald-50 border-emerald-250 text-emerald-700" 
                        : "bg-rose-50 border-rose-250 text-rose-700"
                }`}>
                    <span className={`h-1 w-1 rounded-full ${nvr.status === "online" ? "bg-emerald-500" : "bg-rose-500 animate-pulse"}`} />
                    {nvr.status}
                </span>
            </div>

            {/* Grid details (important specs) */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2.5 border-t border-slate-100/60 text-[10px] text-slate-600 font-semibold">
                <div className="flex flex-col">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Model</span>
                    <span className="text-slate-800 truncate" title={device?.model}>{device?.model || "--"}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Serial Number</span>
                    <span className="text-slate-800 font-mono truncate" title={device?.serial_number}>{device?.serial_number || "--"}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Channels</span>
                    <span className="text-slate-800">{nvr.total_channels ? `${nvr.total_channels} channels` : "--"}</span>
                </div>
                <div className="flex flex-col">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Storage Disks</span>
                    <span className="text-slate-800 flex items-center gap-1">
                        <HardDrive className="w-3 h-3 text-[#422568]" />
                        {nvr.total_disks || 0} disks ({nvr.capacity_tb || 0} TB)
                    </span>
                </div>
                <div className="flex flex-col col-span-2">
                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider mb-0.5">Firmware Version</span>
                    <span className="text-slate-700 truncate" title={`${device?.firmware} (Build ${device?.firmware_build})`}>
                        {device?.firmware || "--"} ({device?.firmware_build || "--"})
                    </span>
                </div>
            </div>

            {/* Health Stats Row */}
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-150 rounded-lg p-2 mt-0.5 text-[9px] font-black uppercase tracking-wider">
                <span className="text-slate-450 mr-1">Disks Health:</span>
                <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                    <ShieldCheck className="w-3 h-3" />
                    OK: {nvr.total_ok || 0}
                </span>
                <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100">
                    <AlertTriangle className="w-3 h-3" />
                    WARN: {nvr.total_warnings || 0}
                </span>
                <span className="flex items-center gap-1 text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded border border-rose-100">
                    <X className="w-3 h-3" />
                    CRIT: {nvr.total_critical || 0}
                </span>
            </div>

            {/* Network details expander */}
            <div className="border-t border-slate-100/60 pt-2 flex flex-col gap-1.5">
                <button
                    onClick={() => setIsNetworkExpanded(!isNetworkExpanded)}
                    className="w-full flex items-center justify-between text-[9px] font-black uppercase tracking-widest text-[#422568] hover:text-[#422568] py-1 px-2 rounded-lg bg-[#422568]/5 hover:bg-[#422568]/10 transition-all"
                >
                    <span className="flex items-center gap-1">
                        <Network className="w-3 h-3" />
                        Network Configuration
                    </span>
                    {isNetworkExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>

                <AnimatePresence>
                    {isNetworkExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden space-y-2 text-[10px] bg-slate-50/50 rounded-lg p-2.5 border border-slate-150"
                        >
                            <div className="grid grid-cols-2 gap-2 pb-2 border-b border-slate-200/50">
                                <div>
                                    <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-wider block mb-0.5">Hostname</span>
                                    <span className="font-mono text-slate-700">{network?.hostname || "--"}</span>
                                </div>
                                <div>
                                    <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-wider block mb-0.5">NTP Client</span>
                                    <span className="font-bold text-slate-700 uppercase">{network?.ntp_enabled ? "Enabled" : "Disabled"}</span>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <span className="text-[7.5px] font-black text-slate-400 uppercase tracking-widest block">Interfaces List</span>
                                {network?.interfaces?.map((iface: any, idx: number) => (
                                    <div key={idx} className="bg-white border border-slate-150 rounded-lg p-2 flex flex-col gap-1 shadow-sm font-mono text-[9px] text-slate-600">
                                        <div className="flex justify-between items-center border-b border-slate-100 pb-1 mb-0.5">
                                            <span className="font-bold text-slate-800 font-sans uppercase tracking-tight">{iface.name}</span>
                                            <span className={`text-[7px] font-black uppercase px-1 rounded ${iface.dhcp_enabled ? 'bg-indigo-50 border border-indigo-100 text-indigo-600' : 'bg-slate-100 border border-slate-200 text-slate-500'}`}>
                                                {iface.dhcp_enabled ? 'DHCP' : 'STATIC'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>IP Addr:</span>
                                            <span className="font-bold text-slate-800">{iface.ip_address}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>MAC Addr:</span>
                                            <span className="text-slate-500">{iface.mac_address}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Footer report time */}
            <div className="flex items-center gap-1 text-[8.5px] text-slate-400 font-bold uppercase tracking-tight border-t border-slate-50 pt-2 mt-0.5">
                <Clock className="w-3 h-3" />
                <span>Last Report: {nvr.report_time}</span>
            </div>
        </div>
    );
};

export const NvrListSidebar: React.FC<NvrListSidebarProps> = ({
    isOpen,
    onClose,
    nvrList = [],
    filter
}) => {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const limit = 5;

    // Reset pagination and search on reopen/filter changes
    useEffect(() => {
        if (isOpen) {
            setSearch("");
            setPage(1);
        }
    }, [isOpen, filter]);

    // Local filter and search logic
    const filteredNvrList = nvrList
        .filter((nvr) => {
            if (filter === "healthy") {
                return nvr.status === "online";
            }
            return true;
        })
        .filter((nvr) => {
            if (!search.trim()) return true;
            const term = search.toLowerCase();
            return (
                nvr.nvr_address?.toLowerCase().includes(term) ||
                nvr.network?.hostname?.toLowerCase().includes(term) ||
                nvr.device?.model?.toLowerCase().includes(term) ||
                nvr.device?.serial_number?.toLowerCase().includes(term)
            );
        });

    const paginatedNvrList = filteredNvrList.slice((page - 1) * limit, page * limit);
    const totalPages = Math.ceil(filteredNvrList.length / limit);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
                    />

                    {/* Sidebar Container */}
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-[500px] bg-white border-l border-slate-200 z-[101] flex flex-col shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-[#422568]/10 text-[#422568]">
                                    <Database className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-sm font-black text-slate-900 uppercase tracking-widest leading-none">
                                        {filter === "healthy" ? "Healthy" : "All"} NVR Assets
                                    </h2>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight mt-1">
                                        Network Video Recorder status and storage diagnostics
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 min-h-0 flex flex-col p-4">
                            {/* Search input */}
                            <div className="relative mb-4 shrink-0">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                                <input
                                    type="text"
                                    placeholder="Search by address, host, model, or serial..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-8 py-2 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#422568]/15 focus:border-[#422568] transition-all"
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPage(1);
                                    }}
                                />
                                {search && (
                                    <button
                                        onClick={() => {
                                            setSearch("");
                                            setPage(1);
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded-full hover:bg-slate-200"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Cards List container */}
                            <div className="flex-1 overflow-y-auto custom-scroll pr-1 space-y-3 mb-2">
                                {paginatedNvrList.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center py-20 text-slate-400 opacity-60">
                                        <Database className="w-10 h-10 mb-2.5" />
                                        <p className="text-[10px] font-black uppercase tracking-wider">No NVRs Found</p>
                                    </div>
                                ) : (
                                    paginatedNvrList.map((nvr: any, index: number) => (
                                        <NvrCard key={nvr.nvr_address || index} nvr={nvr} />
                                    ))
                                )}
                            </div>

                            {/* Pagination footer */}
                            {totalPages > 1 && (
                                <div className="shrink-0 pt-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50 rounded-b-xl px-2 py-1.5">
                                    <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        Page {page} of {totalPages}
                                    </span>
                                    <div className="flex gap-1.5">
                                        <button
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Prev
                                        </button>
                                        <button
                                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            disabled={page >= totalPages}
                                            className="p-1 px-2 rounded bg-white hover:bg-slate-100 border border-slate-200 disabled:opacity-30 disabled:hover:bg-white text-[10px] font-black uppercase tracking-wider transition-all"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
